import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import { findAll } from 'highlight-words-core';
import displayError from '../../utilities/displayError';
import { FormattedMessage } from "react-intl";

const customStyles = {
  control: (provided, { isFocused }) => {
    const focusColors = isFocused ? { boxShadow: 0 } : {};

    return {
      ...provided,
      ...focusColors,
      height: 42,
      borderColor: '#DDE0E6',
      cursor: 'text'
    };
  },

  placeholder: (provided) => {
    return {
      ...provided,
      color: '#CED2DA'
    };
  },

  option: (provided, { isSelected, isFocused }) => ({
    ...provided,
    backgroundColor: isSelected
      ? '#F8F9FA'
      : isFocused
        ? '#F8F9FA'
        : null,
    color: '#343840',
    padding: 8
  }),

  dropdownIndicator: () => ({
    display: 'none'
  }),

  indicatorSeparator: () => ({
    display: 'none'
  }),

  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    boxShadow: 'none',
    border: '1px solid #DDE0E6'
  })
};

const customTheme = (theme) => ({
  ...theme,
  borderRadius: 2,
  colors: {
    ...theme.colors,
    primary: '#DDE0E6',
    primary75: '#E9EBEF',
    primary50: '#E9EBEF',
    primary25: '#E9EBEF'
  }
});

const formatOptionLabel = ({ label }, { inputValue }) => {
  const textToHighlight = label;

  const chunks = findAll({
    caseSensitive: false,
    textToHighlight,
    autoEscape: true,
    searchWords: [inputValue]
  });

  return map(chunks, (chunk) => {
    const { end, highlight, start } = chunk;
    const text = textToHighlight.substr(start, end - start);

    if (highlight) {
      return <b id={`${text}`} className="text-gray-darker">{text}</b>;
    }
    return <span id={`${text}`} className="space-type-value testing">{text}</span>;
  });
};


class SearchField extends Component {
  constructor (props) {
    super(props);

    // Not using state for this value because the setting of this value needs to be sync
    this.value = this.props.input.value;

    this.state = {
      menuIsOpen: false
    };

    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleOnInputChange = this.handleOnInputChange.bind(this);
    this.handleOnFocus = this.handleOnFocus.bind(this);
  }

  handleOnChange (changeValue, { action }) {
    const val = get(changeValue, 'value');

    if ('select-option' === action) {
      this.value = val;
    }

    if (val || 0 === val) {
      this.props.input.onChange(val);
    }
  }

  handleOnInputChange (changeValue, { action }) {
    if ('menu-close' === action) {
      this.setState({ menuIsOpen: false });
    } else if ('input-blur' === action && !find(this.props.options, { value: this.value })) {
      this.props.input.onChange(null);
      this.props.input.onBlur(null);
    } else if ('input-change' === action) {
      this.value = changeValue;
      this.props.input.onChange(changeValue);
      this.setState({ menuIsOpen: !!changeValue });
    }
  }

  handleOnFocus () {
    if (!this.props.input.value) {
      this.props.input.onChange(null);
    }
  }

  render () {
    const {
      className = '',
      inputClassName = '',
      options = [],
      input = {},
      meta = {},
      styles = null,
      placeholder = null,
      errorMessage = null,
      maxMenuHeight = 300,
      id = '',
      attributes = {},
      isDisabled,
      classNamePrefix,
      menuPlacement = 'bottom',
      isSearchable = true,
      noOptionsMessage,
      components
    } = this.props;

    const { error, submitFailed } = meta;
    const searchFieldClassNames = classNames('SearchField', className);
    const { name, value } = input;

    const reduxFormValue = find(options, { value }) || '';
    const noOptions = () => <FormattedMessage id='forms.space_type_form.no_options' defaultMessage='No options'/>

    return (
      <div className={searchFieldClassNames} {...attributes}>
        <Select
          name={name}
          value={reduxFormValue}
          className={inputClassName}
          placeholder={placeholder}
          options={options}
          onChange={this.handleOnChange}
          onInputChange={this.handleOnInputChange}
          styles={styles || customStyles}
          theme={customTheme}
          maxMenuHeight={maxMenuHeight}
          inputId={id}
          isDisabled={isDisabled}
          classNamePrefix={classNamePrefix}
          menuPlacement={menuPlacement}
          onFocus={this.handleOnFocus}
          isClearable={true}
          openMenuOnClick={false}
          menuIsOpen={this.state.menuIsOpen}
          blurInputOnSelect={true}
          formatOptionLabel={formatOptionLabel}
          isSearchable={isSearchable}
          noOptionsMessage={noOptionsMessage || noOptions}
          components={components}
        />

        { !!(error && submitFailed) &&
          <p className="error text-danger text-small">
            {displayError(errorMessage, error)}
          </p>
        }
      </div>
    );
  }
}

SearchField.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  options: PropTypes.array,
  input: PropTypes.object,
  meta: PropTypes.object,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  styles: PropTypes.object,
  id: PropTypes.string,
  removeSelections: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  attributes: PropTypes.object,
  allOptions: PropTypes.array,
  isDisabled: PropTypes.bool,
  classNamePrefix: PropTypes.string,
  menuPlacement: PropTypes.string,
  isSearchable: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  components: PropTypes.object
};

export default SearchField;
